import React, { useState, useEffect, useContext, useMemo } from 'react';
import moment from 'moment';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import WidGetDateSelect from '../componentsWidgets/WidGetDateSelect';
import Loader from '../components/Loader';
import { get } from '../utils/fetcher';

const COLORS = ['#9754e3', '#0074dc', '#15992f', '#f27936', '#f8cc28', '#00d8dc'];

const StatPage = () => {
    const { setCurrentPage } = useContext(AppContext)
    const [date, setDate] = useState(new Date())
    const [entries, setEntries] = useState([])
    const [load, setLoad] = useState(true)
    const [view, setView] = useState('line')
    const [hidden, setHidden] = useState({})

    useEffect(() => { setCurrentPage('stat') }, [])

    const fetchStats = async (d) => {
        setLoad(true)
        const res = await get('/stats/history', { date: moment(d).format('YYYY-MM-DD') })
        setEntries(Array.isArray(res?.entries) ? res.entries : [])
        setLoad(false)
    }
    useEffect(() => { fetchStats(date) }, [])

    // Projets (union des snapshots)
    const projects = useMemo(() => {
        const map = {}
        entries.forEach(e => (e.data || []).forEach(p => { map[p.project] = p.label }))
        return Object.keys(map).map((project, i) => ({ project, label: map[project], color: COLORS[i % COLORS.length] }))
    }, [entries])

    const colorOf = (project) => projects.find(p => p.project === project)?.color || '#9754e3'
    const labelOf = (project) => projects.find(p => p.project === project)?.label || project

    // Données ligne : [{ time, [label]: views }]
    const lineData = useMemo(() => entries.map(e => {
        const point = { time: moment(e.date).format('HH:mm') }
        ;(e.data || []).forEach(p => { point[p.label] = p.views })
        return point
    }), [entries])

    const latest = entries.length ? (entries[entries.length - 1].data || []) : []
    const first = entries.length ? (entries[0].data || []) : []
    const viewsOf = (arr, project) => arr.find(p => p.project === project)?.views ?? 0
    const totalNow = latest.reduce((a, p) => a + (p.views || 0), 0)

    const toggle = (project) => setHidden(h => ({ ...h, [project]: !h[project] }))
    const visible = projects.filter(p => !hidden[p.project])
    const barData = latest.filter(p => !hidden[p.project]).map(p => ({ name: labelOf(p.project), views: p.views, project: p.project }))

    const tooltipStyle = { background: '#1b1822', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }

    return (
        <MainLayout>
            <CenterLayout>
                <div className='stat-page'>
                    <div className='df aic jcsb fw gap1 mb2'>
                        <p className='fb cw fs20'>Statistiques</p>
                        <WidGetDateSelect date={date} setDate={(d) => { setDate(d); fetchStats(d) }} />
                    </div>

                    {load ? (
                        <div className='py2'><Loader /></div>
                    ) : (
                        <>
                            {/* KPI */}
                            <div className='stat-kpis df gap1 fw mb2'>
                                <div className='stat-kpi'><span className='k-label'>Vues actuelles</span><span className='k-val'>{totalNow}</span></div>
                                <div className='stat-kpi'><span className='k-label'>Projets</span><span className='k-val'>{projects.length}</span></div>
                                <div className='stat-kpi'><span className='k-label'>Relevés du jour</span><span className='k-val'>{entries.length}</span></div>
                            </div>

                            {/* Vues */}
                            <div className='df gap1 mb2 fw stat-views'>
                                {[['line', 'Évolution'], ['bar', 'Comparaison'], ['table', 'Tableau']].map(([v, l]) => (
                                    <button key={v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>{l}</button>
                                ))}
                            </div>

                            {/* Filtres projets */}
                            <div className='df gap1 fw mb2 stat-chips'>
                                {projects.map(p => (
                                    <button key={p.project} onClick={() => toggle(p.project)} className={`stat-chip ${hidden[p.project] ? 'off' : ''}`}>
                                        <span className='dot' style={{ background: p.color }}></span>{p.label}
                                    </button>
                                ))}
                            </div>

                            {entries.length === 0 && (
                                <p className='cw' style={{ opacity: 0.5 }}>Aucune donnée pour cette date.</p>
                            )}

                            {entries.length > 0 && view === 'line' && (
                                <div className='stat-chart'>
                                    <ResponsiveContainer width='100%' height={340}>
                                        <LineChart data={lineData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                            <CartesianGrid stroke='rgba(255,255,255,0.06)' vertical={false} />
                                            <XAxis dataKey='time' stroke='#8f909c' fontSize={11} />
                                            <YAxis stroke='#8f909c' fontSize={11} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            {visible.map(p => (
                                                <Line key={p.project} type='monotone' dataKey={p.label} stroke={p.color} strokeWidth={2} dot={false} />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {entries.length > 0 && view === 'bar' && (
                                <div className='stat-chart'>
                                    <ResponsiveContainer width='100%' height={340}>
                                        <BarChart data={barData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                            <CartesianGrid stroke='rgba(255,255,255,0.06)' vertical={false} />
                                            <XAxis dataKey='name' stroke='#8f909c' fontSize={11} />
                                            <YAxis stroke='#8f909c' fontSize={11} />
                                            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={tooltipStyle} />
                                            <Bar dataKey='views' radius={[6, 6, 0, 0]}>
                                                {barData.map((d, i) => (<Cell key={i} fill={colorOf(d.project)} />))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {entries.length > 0 && view === 'table' && (
                                <div className='stat-table'>
                                    <div className='st-head'>
                                        <span>Projet</span>
                                        <span className='tar'>Début</span>
                                        <span className='tar'>Actuel</span>
                                        <span className='tar'>Évol.</span>
                                    </div>
                                    {visible.map(p => {
                                        const f = viewsOf(first, p.project), l = viewsOf(latest, p.project), diff = l - f
                                        return (
                                            <div className='st-row' key={p.project}>
                                                <span className='st-name'><span className='dot' style={{ background: p.color }}></span>{p.label}</span>
                                                <span className='tar cw'>{f}</span>
                                                <span className='tar cw fb'>{l}</span>
                                                <span className='tar st-diff'>
                                                    {diff === 0 ? <Minus size={14} color='#8f909c' /> : diff > 0 ? <><ArrowUp size={14} color='#15992f' /> {diff}</> : <><ArrowDown size={14} color='#f71717' /> {Math.abs(diff)}</>}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </CenterLayout>
        </MainLayout>
    );
};

export default StatPage;
